import "@fontsource-variable/dm-sans/wght.css";
import "@fontsource/instrument-serif/400.css";
import { captureRemixErrorBoundaryError } from "@sentry/remix";
import { QueryClientProvider } from "@tanstack/react-query";
import "node_modules/vaul/style.css";
import { useEffect, useState } from "react";
import { Links, Meta, type MetaFunction, Outlet, Scripts, ScrollRestoration, useRouteError } from "react-router";

import { Analytics } from "~/components/analytics";
import { ErrorComponent } from "~/components/error-component";
import { GoogleTagManagerBody, GoogleTagManagerHead } from "~/components/google-tag-manager";
import { MetaPixel } from "~/components/meta-pixel";
import { Notifications } from "~/components/notifications";
import { SupportDrawer } from "~/components/support/support-drawer";

import "@worship-devo/ui/styles.css";
import "~/tailwind.css";

import { I18nextProvider } from "react-i18next";
import { PlayerActionsContextProvider } from "./components/audio-player/hooks/usePlayerActions";
import { PlayerEngineStateProxy } from "./components/audio-player/hooks/usePlayerEngine";
import { LocaleBootstrap } from "./components/locale-bootstrap";
import { MediaSessionController } from "./components/MediaSessionController";
import { useCaptureInstallPrompt } from "./components/pwa-install-button";
import { queryClient } from "./entry.client";
import { useAnalyticsTrackReferral } from "./hooks/useAnalyticsTrackReferrals";
import { usePageView } from "./hooks/usePageView";
import { captureUtm } from "./lib/captureUtm";
import i18n from "./lib/i18n";
import { cn } from "./lib/utils";
import { useLocale } from "./stores/useLocaleStore";
import { PostSignupSmsOptInStoreProvider } from "./stores/usePostSignupSmsOptInStore";

if (import.meta.env.DEV) {
  import("react-grab");
}
export const meta: MetaFunction = () => {
  return [{ title: "Sing" }];
};

export function clientLoader() {
  captureUtm();
}

export function Layout({ children }: { children: React.ReactNode }) {
  const locale = useLocale();
  const [loadMetaPixel, setLoadMetaPixel] = useState(false);
  useCaptureInstallPrompt();
  usePageView();

  const { getURLSearchParams } = useAnalyticsTrackReferral();
  useEffect(() => {
    void getURLSearchParams();
  }, [getURLSearchParams]);

  useEffect(() => {
    setLoadMetaPixel(true);
  }, []);

  return (
    <html lang={locale} suppressHydrationWarning>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" type="image/png" href="/favicon-96x96.png" sizes="96x96" />
        <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
        <link rel="shortcut icon" href="/favicon.ico" />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
        <link rel="manifest" href="/site.webmanifest" />
        <meta name="apple-mobile-web-app-title" content="Devos" />
        {/* Meta Pixel Code */}
        {loadMetaPixel ? <MetaPixel /> : null}
        <GoogleTagManagerHead />
        <Meta />
        <Links />
      </head>
      <body className={cn("h-full min-h-full bg-twi-black text-foreground")}>
        <GoogleTagManagerBody />
        <div className="mx-auto flex min-h-dvh max-w-screen-sm flex-col lg:max-w-full">{children}</div>
        <Notifications />
        <SupportDrawer />
        <ScrollRestoration />
        <Scripts />
        <Analytics />
      </body>
    </html>
  );
}

export default function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <LocaleBootstrap />
      <PlayerActionsContextProvider>
        <QueryClientProvider client={queryClient}>
          <PostSignupSmsOptInStoreProvider>
            <Outlet />
            <MediaSessionController />
          </PostSignupSmsOptInStoreProvider>
          {/* {process.env.NODE_ENV === "development" ? <ReactQueryDevtools initialIsOpen={false} /> : null} */}
        </QueryClientProvider>
        <PlayerEngineStateProxy />
      </PlayerActionsContextProvider>
    </I18nextProvider>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();
  captureRemixErrorBoundaryError(error);
  return (
    <html lang="en" className="h-full">
      <head>
        <title>Oh no!</title>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
        <link rel="manifest" href="/site.webmanifest" />
        <GoogleTagManagerHead />
        <Meta />
        <Links />
      </head>
      <body>
        <GoogleTagManagerBody />
        <div className="px-6 py-24 sm:py-32 lg:px-8">
          <ErrorComponent />
        </div>
        <Scripts />
        <Analytics />
      </body>
    </html>
  );
}

export function HydrateFallback() {
  return (
    <div className="fade-in-0 absolute inset-0 flex animate-in items-center justify-center bg-twi-black">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
        className="size-12 animate-spin text-primary ease-in-out"
      >
        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
        <path d="M12 3a9 9 0 1 0 9 9" />
      </svg>
    </div>
  );
}
